import { useMemo, type ReactNode } from 'react';
import { useDispatch } from 'react-redux';
import { Text } from '@rsos/base-components';
import { Button } from '@rsos/components/capstone/base';
import { setPreselectedTrain } from '@rsos/reduxDataStore/src/features/norfolkSouthern/norfolkSouthernSlice';
import {
  setSelectedRequest,
  type NorfolkSouthernRequestResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { formatDateWithLocalTimezone } from '../../../../../utils/dateUtilities';
import {
  formatCount,
  formatFeet,
  formatMph,
  formatOriginDate,
} from '../../../../../utils/norfolkSouthern/format';
import ConsistCarSections from './ConsistCarSections';
import styles from './NorfolkSouthernStatus.module.css';
import NorfolkSouthernStatusHeader from './NorfolkSouthernStatusHeader';
import RelatedCallBox from './RelatedCallBox';
import {
  EMPTY_VALUE,
  formatRequesterName,
  trainSelectionFromSnapshot,
} from './norfolkSouthernStatusUtils';

interface DetailProps {
  label: string;
  children: ReactNode;
}

function Detail({ label, children }: DetailProps): JSX.Element {
  return (
    <div className={styles.detail}>
      <Text size="small" color="primaryText" fontWeight="bold">
        {label}
      </Text>
      <div className={styles.detailValue}>
        <Text size="normal" color="secondaryText">
          {children}
        </Text>
      </div>
    </div>
  );
}

interface InfoRow {
  label: string;
  value: string | null | undefined;
}

function InfoBox({ title, rows }: { title: string; rows: InfoRow[] }) {
  return (
    <div className={styles.infoBox} data-name="norfolk-southern-train-info">
      <Text size="small" color="primaryText" fontWeight="bold">
        {title}
      </Text>
      {rows.map(row => (
        <div key={row.label} className={styles.infoRow}>
          <span className={styles.infoLabel}>{row.label}</span>
          <span className={styles.infoValue}>{row.value || EMPTY_VALUE}</span>
        </div>
      ))}
    </div>
  );
}

interface NorfolkSouthernStatusProps {
  request: NorfolkSouthernRequestResponse;
}

/**
 * Detail view for a consist request: the outcome banner, the train as
 * reported with the consist, every hazmat car up top with its ERG guide,
 * the full car list, who asked for it and why, and the call it was raised
 * for. A failed fetch shows the failure and offers a retry, which is a new
 * request for the same train.
 */
function NorfolkSouthernStatus({
  request,
}: NorfolkSouthernStatusProps): JSX.Element {
  const dispatch = useDispatch();
  const transformed = request.request;
  const { train: snapshot, consist, requester } = transformed;
  const isFailed = transformed.consistState === 'failed';

  const consistTrain = consist?.train;
  const trainRows = useMemo((): InfoRow[] => {
    const route = [consistTrain?.origin, consistTrain?.destination]
      .filter(Boolean)
      .join(' → ');
    return [
      { label: 'Train ID', value: snapshot.trainId },
      { label: 'Train Symbol', value: snapshot.trainSymbol },
      { label: 'Origin Date', value: formatOriginDate(snapshot.originDate) },
      {
        label: 'Number of Cars',
        value: formatCount(consistTrain?.numberOfCars ?? snapshot.numberOfCars),
      },
      {
        label: 'Length',
        value: formatFeet(consistTrain?.lengthFeet ?? snapshot.trainLengthFeet),
      },
      {
        label: 'Loaded Railcars',
        value: formatCount(consistTrain?.numberOfLoads),
      },
      {
        label: 'Empty Railcars',
        value: formatCount(consistTrain?.numberOfEmpties),
      },
      {
        label: 'Hazmat Cars',
        value: formatCount(consistTrain?.numberOfHazmatCars),
      },
      {
        label: 'Speed',
        value: formatMph(consistTrain?.speedMph ?? snapshot.speedMph),
      },
      { label: 'Route', value: route || null },
      { label: 'Direction', value: consistTrain?.travelDirection },
      {
        label: 'Reported',
        value: consistTrain?.reportedAt
          ? formatDateWithLocalTimezone(
              consistTrain.reportedAt,
              'MM/DD/YY HH:mm',
            )
          : null,
      },
    ].filter(row => isFailed || row.value);
  }, [consistTrain, snapshot, isFailed]);

  const handleRetry = (): void => {
    dispatch(setPreselectedTrain(trainSelectionFromSnapshot(snapshot)));
    dispatch(setSelectedRequest(null));
  };

  return (
    <div className={styles.status} data-name="norfolk-southern-status">
      <NorfolkSouthernStatusHeader request={request} />

      <Detail label="Requested by">{formatRequesterName(requester)}</Detail>
      <Detail label="Reason">{transformed.reason}</Detail>

      <InfoBox title="Train Information" rows={trainRows} />

      <ConsistCarSections
        cars={consist?.cars ?? []}
        isFailed={isFailed}
        snapshot={snapshot}
      />

      <RelatedCallBox callerId={request.callerId} />

      {isFailed && (
        <div className={styles.buttons}>
          <Button
            grow
            btnType="secondary"
            type="button"
            onClick={handleRetry}
            dataName="norfolk-southern-status-retry-button"
          >
            Retry Request
          </Button>
        </div>
      )}
    </div>
  );
}

export default NorfolkSouthernStatus;
